import React, { createContext, useState, useContext, ReactNode, useMemo, useCallback } from 'react';

interface TowerLayoutContextType {
    layoutContentIcon: ReactNode;
    setLayoutContentIcon: (content: ReactNode) => void;
    layoutContentLeft: ReactNode;
    setLayoutContentLeft: (content: ReactNode) => void;
    layoutContent: ReactNode;
    setLayoutContent: (content: ReactNode) => void;
    layoutHeader: string;
    setLayoutHeader: (content: string) => void;
    hideHeader: boolean;
    setHideHeader: (value: boolean) => void;
    hideTowerMenu: boolean;
    setHideTowerMenu: (value: boolean) => void;
    tabTitle: string;
    setTabTitle: (content: string) => void;
    isOfflineMode: boolean | undefined;
    setIsOfflineMode: (isOffline: boolean) => void;
    clearLayout: () => void;
    isBreakingPointsEnabled: boolean;
    setIsBreakingPointsEnabled: (isEnabled: boolean) => void;
    hideReportsButton: boolean;
    setHideReportsButton: (value: boolean) => void;
}

export const LayoutContext = createContext<TowerLayoutContextType | undefined>(undefined);

export const TowerLayoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [layoutHeader, setLayoutHeader] = useState<string>('');
    const [layoutContent, setLayoutContent] = useState<ReactNode>();
    const [layoutContentLeft, setLayoutContentLeft] = useState<ReactNode>();
    const [layoutContentIcon, setLayoutContentIcon] = useState<ReactNode>();
    const [hideHeader, setHideHeader] = useState<boolean>(false);
    const [hideTowerMenu, setHideTowerMenu] = useState<boolean>(false);
    const [tabTitle, setTabTitle] = useState<string>('');
    const [isOfflineMode, setIsOfflineMode] = useState<boolean | undefined>();
    const [isBreakingPointsEnabled, setIsBreakingPointsEnabled] = useState<boolean>(false);
    const [hideReportsButton, setHideReportsButton] = useState<boolean>(false);

    const clearLayout = useCallback(() => {
        setLayoutHeader('');
        setLayoutContent(undefined);
        setLayoutContentLeft(undefined);
        setLayoutContentIcon(undefined);
        setHideHeader(false);
        setHideTowerMenu(false);
        setTabTitle('');
        setIsOfflineMode(undefined);
    }, []);

    const value = useMemo(
        () => ({
            layoutContent,
            setLayoutContent,
            layoutHeader,
            setLayoutHeader,
            layoutContentLeft,
            setLayoutContentLeft,
            layoutContentIcon,
            setLayoutContentIcon,
            hideHeader,
            setHideHeader,
            hideTowerMenu,
            setHideTowerMenu,
            tabTitle,
            setTabTitle,
            isOfflineMode,
            setIsOfflineMode,
            clearLayout,
            isBreakingPointsEnabled,
            setIsBreakingPointsEnabled,
            hideReportsButton,
            setHideReportsButton
        }),
        [
            layoutContent,
            layoutHeader,
            layoutContentLeft,
            layoutContentIcon,
            hideHeader,
            hideTowerMenu,
            tabTitle,
            isOfflineMode,
            clearLayout,
            isBreakingPointsEnabled
        ]
    );

    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useTowerLayout = (): TowerLayoutContextType => {
    const context = useContext(LayoutContext);
    if (!context) {
        throw new Error('useTowerLayout must be used within a LayoutProvider');
    }
    return context;
};
